import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

export interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
}

/**
 * Route guardian component to protect pages from unauthorized or under-privileged users.
 */
export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps): React.JSX.Element | null {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // If session is still loading, display an aesthetic spinner matching the theme
  if (isLoading) {
    return (
      <div className="min-h-screen w-full bg-slate-50 flex flex-col items-center justify-center">
        <Loader2 className="w-10 h-10 text-emerald-700 animate-spin" />
        <p className="mt-4 text-sm text-slate-500 font-medium">Проверка авторизации...</p>
      </div>
    );
  }

  // If not authenticated, redirect to login page with location history
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If authenticated but role isn't matching the allowed roles, redirect to 403 Forbidden
  if (allowedRoles && allowedRoles.length > 0) {
    if (!user) {
      return <Navigate to="/403" replace />;
    }
    const userRoles = (user.role || '').split(',').map(r => r.trim()).filter(Boolean);
    const hasAllowedRole = userRoles.some(r => allowedRoles.includes(r));
    const isAppAdmin = userRoles.includes('app_admin');
    if (!hasAllowedRole && !isAppAdmin) {
      return <Navigate to="/403" replace />;
    }
  }

  // Render original component if all checks pass
  return <>{children}</>;
}
